import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Undo as ReconsiderIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import LoadingSpinner from '../components/common/LoadingSpinner';
import UserService from '../services/userService';
import { useAuth } from '../context/AppContext';

interface Student {
  id: string;
  name: string;
  email: string;
  branch: string;
  appliedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  approvedBy?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`student-tabpanel-${index}`}
      aria-labelledby={`student-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const StudentApprovalPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [pendingStudents, setPendingStudents] = useState<Student[]>([]);
  const [approvedStudents, setApprovedStudents] = useState<Student[]>([]);
  const [rejectedStudents, setRejectedStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    type: 'approve' | 'reject' | 'reconsider';
    student: Student | null;
  }>({ open: false, type: 'approve', student: null });

  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadAllStudents();
  }, []);

  const loadAllStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all three categories of students in parallel
      const [pending, approved, rejected] = await Promise.all([
        UserService.getPendingStudents(),
        UserService.getApprovedStudents(),
        UserService.getRejectedStudents(),
      ]);

      setPendingStudents(pending);
      setApprovedStudents(approved);
      setRejectedStudents(rejected);
    } catch (error: any) {
      console.error('Failed to load students:', error);
      setError('Failed to load student data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveStudent = async () => {
    if (!actionDialog.student) return;

    try {
      setActionLoading(true);
      await UserService.approveStudent(actionDialog.student.id);
      setActionDialog({ open: false, type: 'approve', student: null });
      loadAllStudents(); // Refresh data
    } catch (error: any) {
      console.error('Failed to approve student:', error);
      setError('Failed to approve student. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectStudent = async () => {
    if (!actionDialog.student) return;

    try {
      setActionLoading(true);
      await UserService.rejectStudent(actionDialog.student.id, rejectionReason);
      setActionDialog({ open: false, type: 'reject', student: null });
      setRejectionReason('');
      loadAllStudents(); // Refresh data
    } catch (error: any) {
      console.error('Failed to reject student:', error);
      setError('Failed to reject student. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReconsiderStudent = async () => {
    if (!actionDialog.student) return;

    try {
      setActionLoading(true);
      await UserService.reconsiderStudent(actionDialog.student.id);
      setActionDialog({ open: false, type: 'reconsider', student: null });
      loadAllStudents(); // Refresh data
    } catch (error: any) {
      console.error('Failed to reconsider student:', error);
      setError('Failed to reconsider student. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderStudentList = (students: Student[], showActions: boolean = false, actionType?: 'approve' | 'reject' | 'reconsider') => {
    if (students.length === 0) {
      return (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              No Students Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeTab === 0 && 'No pending student applications at this time.'}
              {activeTab === 1 && 'No approved students yet.'}
              {activeTab === 2 && 'No rejected students.'}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <List sx={{ bgcolor: 'background.paper' }}>
        {students.map((student) => (
          <Card key={student.id} sx={{ mb: 2 }}>
            <ListItem sx={{ p: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: { xs: '100%', sm: 'auto' }, flex: { xs: 0, sm: 1 }, minWidth: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 }, bgcolor: 'primary.main' }}>
                    {student.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  sx={{ ml: 2, minWidth: 0 }}
                  primary={
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}>
                        {student.name}
                      </Typography>
                      <Chip
                        icon={<SchoolIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                        label={student.branch}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' }, wordBreak: 'break-word' }}>
                        Email: {student.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Applied: {formatDate(student.appliedAt)}
                      </Typography>
                      {student.approvedAt && student.approvedBy && (
                        <Typography variant="body2" color="success.main" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          Approved: {formatDate(student.approvedAt)} by {student.approvedBy}
                        </Typography>
                      )}
                      {student.rejectedAt && student.rejectedBy && (
                        <Box>
                          <Typography variant="body2" color="error.main" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            Rejected: {formatDate(student.rejectedAt)} by {student.rejectedBy}
                          </Typography>
                          {student.rejectionReason && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                <strong>Reason:</strong> {student.rejectionReason}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </Box>

              {showActions && (
                <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column', md: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                  {actionType === 'approve' && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ApproveIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                        onClick={() => setActionDialog({ open: true, type: 'approve', student })}
                        size="small"
                        sx={{
                          flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 auto' },
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          minWidth: { xs: 'auto', sm: 'fit-content' }
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<RejectIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                        onClick={() => setActionDialog({ open: true, type: 'reject', student })}
                        size="small"
                        sx={{
                          flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 auto' },
                          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                          minWidth: { xs: 'auto', sm: 'fit-content' }
                        }}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {actionType === 'reconsider' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<ReconsiderIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                      onClick={() => setActionDialog({ open: true, type: 'reconsider', student })}
                      size="small"
                      sx={{
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                        minWidth: { xs: 'auto', sm: 'fit-content' }
                      }}
                    >
                      Reconsider
                    </Button>
                  )}
                </Box>
              )}
            </ListItem>
          </Card>
        ))}
      </List>
    );
  };

  if (loading) {
    return <LoadingSpinner fullScreen message="Loading student data..." />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: 4 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
            Student Approval
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
            Manage student registration approvals
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAllStudents}
          disabled={loading}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Permission Info Card */}
      {user && (user as any).permissions && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <InfoIcon sx={{ fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Approval Permissions
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                You can approve students from:{' '}
                {(user as any).permissions.allowedBranches?.length > 0 ? (
                  (user as any).permissions.allowedBranches.map((branch: string, index: number) => (
                    <Chip
                      key={branch}
                      label={branch}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        ml: index > 0 ? 0.5 : 1,
                        fontSize: '0.75rem',
                      }}
                    />
                  ))
                ) : (
                  <Chip
                    label={user.branch || 'Your Branch'}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      ml: 1,
                      fontSize: '0.75rem',
                    }}
                  />
                )}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 }, mb: 4 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
            <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
              {pendingStudents.length}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              Pending Approval
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
            <Typography variant="h3" color="success.main" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
              {approvedStudents.length}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              Approved Students
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 } }}>
            <Typography variant="h3" color="error.main" sx={{ fontWeight: 700, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
              {rejectedStudents.length}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              Rejected Students
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          aria-label="student approval tabs"
          variant="fullWidth"
        >
          <Tab label={`Pending (${pendingStudents.length})`} />
          <Tab label={`Approved (${approvedStudents.length})`} />
          <Tab label={`Rejected (${rejectedStudents.length})`} />
        </Tabs>
      </Card>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {renderStudentList(pendingStudents, true, 'approve')}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderStudentList(approvedStudents)}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {renderStudentList(rejectedStudents, true, 'reconsider')}
      </TabPanel>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, type: 'approve', student: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type === 'approve' && 'Approve Student'}
          {actionDialog.type === 'reject' && 'Reject Student'}
          {actionDialog.type === 'reconsider' && 'Reconsider Student'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.student && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {actionDialog.type === 'approve' &&
                  `Are you sure you want to approve ${actionDialog.student.name}? They will gain access to the platform.`}
                {actionDialog.type === 'reject' &&
                  `Are you sure you want to reject ${actionDialog.student.name}? Please provide a reason below.`}
                {actionDialog.type === 'reconsider' &&
                  `Are you sure you want to move ${actionDialog.student.name} back to pending status for reconsideration?`}
              </Typography>

              {actionDialog.type === 'reject' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rejection Reason"
                  placeholder="Please provide a reason for rejection..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setActionDialog({ open: false, type: 'approve', student: null });
              setRejectionReason('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionDialog.type === 'approve' ? 'success' : actionDialog.type === 'reject' ? 'error' : 'primary'}
            onClick={() => {
              if (actionDialog.type === 'approve') handleApproveStudent();
              else if (actionDialog.type === 'reject') handleRejectStudent();
              else if (actionDialog.type === 'reconsider') handleReconsiderStudent();
            }}
            disabled={actionLoading || (actionDialog.type === 'reject' && !rejectionReason.trim())}
          >
            {actionLoading ? 'Processing...' :
             actionDialog.type === 'approve' ? 'Approve' :
             actionDialog.type === 'reject' ? 'Reject' : 'Reconsider'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StudentApprovalPage;